
import type { Clip } from '@/@types/graphql-types';
import type { WebLocale } from '@/enums/web-locale';
import type { YoutubePlayerOptions } from '@/interfaces/components/youtube-player';

import TitleClips from '@/components/details/TitleClips.vue';
import PictureComp from '@/components/picture/PictureComp.vue';

import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';

import { ClipProvider } from '@/@types/graphql-types';
import { namespace } from 'vuex-class';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

const VideobusterPlayer = () => import('@/ui-components/VideobusterPlayer.vue');
const YoutubePlayerGraphql = () => import('@/ui-components/YoutubePlayerGraphql.vue');
const DailyMotionPlayer = () => import('@/ui-components/DailyMotionPlayer.vue');
const PlexPlayer = () => import('@/ui-components/PlexPlayer.vue');
const VideoPlayerWrapper = () => import('@/ui-components/VideoPlayerWrapper.vue');

const Routing = namespace('routing');
const Language = namespace('language');

@Component({
	name: 'BackdropTrailer',
	components: { YoutubePlayerGraphql, PictureComp, VideobusterPlayer, VideoPlayerWrapper },
})
// @ts-expect-error Because the extended mixin is using CAPI
export default class BackdropTrailer extends Mixins(TitleClips) {
	isMounted = false;

	@Prop({ default: false }) titleName: string;
	@Prop({ type: String, default: '' }) imageUrl: string;
	@Prop({ type: Boolean, default: false }) isTrailerPlaying: boolean;
	@Prop({ required: false, default: false }) isDisneyPlusOffer: boolean;

	@Routing.State activeRoute: any;

	@Language.State webLocale: WebLocale;

	// because the extended TitleClips mixin is using CAPI
	declare clips: Clip[];
	declare mute: boolean | undefined;
	declare source: string;
	declare visibleClips: Clip[];
	declare autoplay?: boolean | undefined;

	@Watch('isTrailerPlaying')
	onTrailerPlayingChange(isPlaying: boolean) {
		if (!isPlaying) {
			this.$emit('activate', null);
		}
	}

	mounted() {
		this.isMounted = true;

		TrackingHelper.trackEvent('clip_spinner', {
			action: 'finished',
			value: 0,
			property: this.visibleClips.length > 0 ? this.visibleClips[0].provider : 'none',
			nonInteraction: true,
		});
	}

	get playerVars(): YoutubePlayerOptions {
		return {
			autoplay: this.autoplay ? 1 : 0,
			mute: this.mute ? 1 : 0,
		};
	}

	get clipsProvider() {
		return this.clips[0]?.provider as ClipProvider | 'PLEX' | 'JUSTWATCH_TV';
	}

	get videoPlayer() {
		if (this.clipsProvider === ClipProvider.Videobuster) {
			return VideobusterPlayer;
		} else if (this.clipsProvider === ClipProvider.Dailymotion || this.clipsProvider === 'JUSTWATCH_TV') {
			return DailyMotionPlayer;
		} else if (this.clipsProvider === 'PLEX') {
			return PlexPlayer;
		}

		return YoutubePlayerGraphql;
	}

	onActivate(clipId: string | null) {
		// the same trailer can be in the backdrop and clips area
		const id = clipId ? `${this.source}:${clipId}` : null;
		this.$emit('activate', id);
	}
}
